import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Camera, Crosshair, Store, CheckCircle2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { getPos, fmtDT } from "@/lib/qr";
import { COUNTRIES, CITIES_BY_COUNTRY, nearCountries } from "@/lib/places";
import { useAuth } from "@/context/AuthContext";
import { CityInput, AddressInput } from "@/components/AddressFields";

const fld = "w-full h-11 rounded-xl border border-slate-200 px-3 text-sm bg-white";
const ST = { pending: ["Në kontroll", "bg-amber-100 text-amber-700"], review: ["Në kontroll", "bg-amber-100 text-amber-700"], published: ["U publikua", "bg-emerald-100 text-emerald-700"], rejected: ["Nuk u publikua", "bg-rose-100 text-rose-600"] };

function ImgPick({ label, value, onChange, testid, round }) {
  const [busy, setBusy] = useState(false);
  const up = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); onChange(r.data.url); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <label className={`relative flex flex-col items-center justify-center gap-1 bg-slate-50 border-2 border-dashed border-slate-200 cursor-pointer overflow-hidden ${round ? "w-24 h-24 rounded-full" : "w-full h-40 rounded-2xl"}`} data-testid={testid}>
      {value ? <img src={value} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <><Camera className="w-6 h-6 text-slate-400" /><span className="text-[11px] text-slate-500 text-center px-2">{busy ? "Duke ngarkuar..." : label}</span></>}
      <input type="file" accept="image/*" className="hidden" onChange={(e) => up(e.target.files?.[0])} data-testid={`${testid}-input`} />
    </label>
  );
}

function Mine({ list }) {
  if (!list.length) return null;
  return (
    <div className="space-y-2" data-testid="suggest-mine">
      <div className="text-sm font-bold text-slate-900">Bizneset që ke treguar</div>
      {list.map((x) => (
        <div key={x.id} className="bg-white rounded-2xl p-3 flex items-center gap-3 border border-slate-100" data-testid={`suggest-mine-${x.id}`}>
          {x.photo && <img src={x.photo} alt="" className="w-12 h-12 rounded-xl object-cover" />}
          <div className="flex-1 min-w-0"><div className="text-sm font-semibold break-words" translate="no">{x.name}</div><div className="text-[11px] text-slate-400">{fmtDT(x.created_at)}{x.points ? ` · +${x.points} kredite` : ""}</div></div>
          <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${ST[x.status]?.[1]}`}>{ST[x.status]?.[0]}</span>
        </div>
      ))}
    </div>
  );
}

export default function SuggestBusinessPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [f, setF] = useState({ name: "", photo: "", logo: "", address: "", city: user?.city || "", country: user?.country || "", note: "", lat: null, lng: null });
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const s = (p) => setF((x) => ({ ...x, ...p }));
  const load = () => api.get("/suggestions/mine").then((r) => setMine(r.data)).catch(() => {});
  useEffect(() => { if (user) load(); }, [user]);
  if (!user) return <div className="p-8 text-center text-slate-500">Hyr në llogari për të treguar një biznes.</div>;
  const locate = async () => { const p = await getPos(); if (p.lat) { s(p); toast.success("Vendndodhja u mor"); } else toast.error("Lejo vendndodhjen (GPS)"); };
  const send = async () => {
    setBusy(true);
    try { await api.post("/suggestions/business", f); setDone(true); load(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="px-4 pt-3 pb-28 space-y-3" data-testid="suggest-page">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-slate-700 font-semibold" data-testid="suggest-back"><ArrowLeft className="w-5 h-5" />Trego një biznes</button>
      {done ? (
        <div className="bg-white rounded-3xl p-6 text-center border border-slate-100" data-testid="suggest-done">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
          <div className="text-xl font-black mt-2">Faleminderit!</div>
          <p className="text-sm text-slate-500 mt-1">Ekipi ynë do ta kontrollojë. Kur biznesi publikohet, merr një mesazh dhe GjejKredit.</p>
          <button data-testid="suggest-another" onClick={() => { setDone(false); setF({ ...f, name: "", photo: "", logo: "", address: "", postal_code: "", note: "", lat: null, lng: null }); }} className="mt-3 h-11 px-5 rounded-xl bg-emerald-500 text-white font-bold">Trego një tjetër</button>
        </div>
      ) : (
        <div className="bg-white rounded-3xl p-4 border border-slate-100 space-y-3">
          <div className="flex items-center gap-2 bg-emerald-50 text-emerald-800 rounded-2xl p-3 text-sm"><Store className="w-5 h-5 shrink-0" />Njeh një biznes shqiptar që s'është në GjejShqip? Na e trego dhe fito GjejKredit kur publikohet.</div>
          <ImgPick label="Foto e biznesit *" value={f.photo} onChange={(v) => s({ photo: v })} testid="suggest-photo" />
          <div className="flex items-center gap-3"><ImgPick label="Logo" value={f.logo} onChange={(v) => s({ logo: v })} testid="suggest-logo" round /><span className="text-xs text-slate-500">Logoja e biznesit (opsionale)</span></div>
          <input data-testid="suggest-name" value={f.name} onChange={(e) => s({ name: e.target.value })} placeholder="Emri i biznesit *" className={fld} />
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="suggest-country" value={f.country} onChange={(e) => s({ country: e.target.value, city: "" })} className={fld}><option value="">Shteti</option>{nearCountries().map((c) => <option key={c.name} value={c.name}>{c.flag} {c.name}</option>)}</select>
            <CityInput testid="suggest-city" country={f.country} value={f.city} onChange={(v) => s({ city: v })} cities={Object.keys(CITIES_BY_COUNTRY[f.country] || {})} setCoords={(c) => s(c)} className={fld} />
          </div>
          <AddressInput testid="suggest-address" f={f} setF={setF} setCoords={(c) => s(c)} placeholder="Adresa e saktë (rruga, numri)" className={fld} />
          <button data-testid="suggest-locate" type="button" onClick={locate} className={`w-full h-11 rounded-xl text-sm font-bold flex items-center justify-center gap-2 border ${f.lat ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-slate-200 text-slate-700"}`}><Crosshair className="w-4 h-4" />{f.lat ? "Vendndodhja u mor ✓" : "Jam te biznesi: përdor vendndodhjen time"}</button>
          <textarea data-testid="suggest-note" value={f.note} onChange={(e) => s({ note: e.target.value })} placeholder="Shënim për ne (opsional), p.sh. çfarë ofron, telefoni" rows={2} className="w-full rounded-xl border border-slate-200 p-3 text-sm" />
          <button data-testid="suggest-send" disabled={busy} onClick={send} className="w-full h-12 rounded-xl bg-emerald-500 text-white font-bold disabled:opacity-60">{busy ? "Duke dërguar..." : "Dërgo"}</button>
        </div>
      )}
      <Mine list={mine} />
    </div>
  );
}
